import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Avatar, Box, Button, Divider, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  companyJobsCountLoadingSelector,
  companyJobsCountSelector,
  fetchCompanyJobsCount
} from '../../../../redux/features/job/jobsSlice';
import { buildCompanyUrl } from '../../../../helpers/urlHelpers';

const useStyles = makeStyles((theme) => ({
  companySidebar: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4)
  },
  companyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  companyLogo: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  companyName: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5)
  },
  metadataItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  metadataLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.875rem'
  },
  metadataValue: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.875rem'
  },
  icon: {
    fontSize: '1.25rem',
    lineHeight: 1
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  actionButton: {
    width: '100%',
    marginTop: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600
  },
  viewCompanyButton: {},
  websiteButton: {}
}));

const CompanySidebarInfo = ({ company, logoUrl }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  // Get jobs count from Redux
  const jobsCount = useSelector(companyJobsCountSelector(company?.id));
  const loadingJobsCount = useSelector(
    companyJobsCountLoadingSelector(company?.id)
  );

  useEffect(() => {
    if (company && company.id) {
      console.log(
        '🔍 [CompanySidebarInfo] Dispatching fetchCompanyJobsCount for company:',
        company.id
      );
      dispatch(fetchCompanyJobsCount({ companyId: company.id }));
    }
  }, [company, dispatch]);

  if (!company || !company.id) {
    return null;
  }

  const handleViewCompany = () => {
    history.push(buildCompanyUrl(company.id, company.name));
  };

  // Helper to safely get hostname from URL
  const getWebsiteHostname = (url) => {
    if (!url) return '';
    try {
      // Add protocol if missing
      const urlWithProtocol =
        url.startsWith('http://') || url.startsWith('https://')
          ? url
          : `https://${url}`;
      const hostname = new URL(urlWithProtocol).hostname;
      return hostname.replace('www.', '');
    } catch (error) {
      // If URL parsing fails, return the original URL
      return url.replace(/^(https?:\/\/)?(www\.)?/, '');
    }
  };

  return (
    <Box className={classes.companySidebar}>
      {/* Company Header */}
      <Box className={classes.companyHeader}>
        {logoUrl ? (
          <Avatar
            src={logoUrl}
            alt={company.name}
            className={classes.companyLogo}
          />
        ) : (
          <Avatar className={classes.companyLogo}>
            <span
              role="img"
              aria-label="office building"
              style={{ fontSize: '2rem' }}>
              🏢
            </span>
          </Avatar>
        )}
        <Box>
          <Typography className={classes.companyName}>
            {company.name}
          </Typography>
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* Company Stats */}
      {!loadingJobsCount && jobsCount > 0 && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Jobs posted</span>
          </Box>
          <Typography className={classes.metadataValue}>{jobsCount}</Typography>
        </Box>
      )}

      {company.location && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Location</span>
          </Box>
          <Typography className={classes.metadataValue}>
            {company.location}
          </Typography>
        </Box>
      )}

      {company.siteURL && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Website</span>
          </Box>
          <Typography
            className={classes.metadataValue}
            component="a"
            href={
              company.siteURL.startsWith('http')
                ? company.siteURL
                : `https://${company.siteURL}`
            }
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer'
            }}>
            {getWebsiteHostname(company.siteURL)}
          </Typography>
        </Box>
      )}

      {/* Action Button */}
      <Button
        className={`${classes.actionButton} ${classes.viewCompanyButton}`}
        variant="outlined"
        color="secondary"
        onClick={handleViewCompany}>
        View company profile
      </Button>
    </Box>
  );
};

CompanySidebarInfo.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    siteURL: PropTypes.string,
    location: PropTypes.string,
    jobsPostedCount: PropTypes.number
  }),
  logoUrl: PropTypes.string
};

CompanySidebarInfo.defaultProps = {
  company: null,
  logoUrl: ''
};

export default CompanySidebarInfo;
